import React from 'react'
import { Box, Button, Icon, Loading, Text } from '@maui/elements'
import Request from './Request'
import { useTable, usePagination } from 'react-table'
import PropTypes from 'prop-types'
export function RequestsList({
  columns,
  data,
  loading,
  totalCount,
  error,
  handleShowServiceDetail
}) {
  const {
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable({
    columns,
    data,
    initialState: { pageIndex: 0, pageSize: 8 },
    autoResetPage: false
  }, usePagination)

  if (error || !data || data?.length === 0) {
    return <DisplayMessage data={data} error={error} />
  }

  return (
    <>
      {!data
        ? <Loading />
        : <>
          {
            page.map((row, i) => {
              prepareRow(row)
              return (
                <div key={i} {...row.getRowProps()} >
                  {row.cells.map((cell, j) => {
                    return (
                      <div key={`${i}-${j}`} >
                        <Request key={j} data={cell.row.original} showServiceDetail={handleShowServiceDetail} loading={loading}/>
                      </div>
                    )
                  })}
                </div>
              )
            })
          }
          <Pagination
            pageIndex={pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            canPreviousPage={canPreviousPage}
            previousPage={previousPage}
            canNextPage={canNextPage}
            nextPage={nextPage}
            gotoPage={gotoPage}
            pageOptions={pageOptions}
            setPageSize={setPageSize}
            totalCount={totalCount}
            loading={loading}
          />
        </>
      }
    </>
  )
}

RequestsList.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  fetchData: PropTypes.func,
  loading: PropTypes.bool,
  pageCount: PropTypes.number,
  totalCount: PropTypes.number,
  error: PropTypes.string,
  handleShowServiceDetail: PropTypes.func
}

function DisplayMessage({ data, error }) {
  if (error?.status === 400 || error?.body?.error?.detail.includes('No Salesforce contact info found')) {
    return <Text variant='heading3'>Please contact mandiant-support@google.com to complete your Expertise On Demand user information setup</Text>
  } else if (!data?.cases?.length && error) {
    return <Text variant='heading3'>Error fetching cases</Text>
  } else if (!data.length) {
    return (
      <Text variant='heading3'>
        <Loading />
      </Text>
    )
  } else if (!data?.cases?.length) {
    return (
      <Text variant='heading3'>
                There are currently no service requests to display.
      </Text>
    )
  }
}

const PageOptions = ({ pageOptions = [], gotoPage, pageIndex, loading }) => {
  let pageOptionsArray = [...pageOptions]
  if (pageOptionsArray.length > 6) {
    pageOptionsArray = [...pageOptions.slice(pageIndex, pageIndex + 5)].concat(['...', pageOptions[pageOptions.length - 1]])
    const withOutLast2Items = pageOptionsArray.slice(0, pageOptionsArray.length - 2)
    const lastItemInPageOptionsArray = pageOptionsArray[pageOptionsArray.length - 1]
    const itemBeforeEllipsis = withOutLast2Items[withOutLast2Items.length - 1]
    if (itemBeforeEllipsis + 1 === lastItemInPageOptionsArray ||
            withOutLast2Items.indexOf(lastItemInPageOptionsArray) > -1 ||
            pageIndex === lastItemInPageOptionsArray) {
      pageOptionsArray = [pageOptions[0], '...', ...pageOptions.slice(-6)]
    }
  }

  return (
    <Box display='flex' flexDirection='row' justifyContent='flex-end' alignItems='center' width='100%' height={40}>
      {pageOptionsArray.map((page, i) => {
        const isButton = page !== '...'
        return (
          <Box display="inline-flex" key={i}>
            {isButton ? <Button size="small" variant="secondaryText" key={i} onClick={() => gotoPage(page)} bg={page === pageIndex && 'colorFillEmphasis'} disabled={loading}>{page + 1}</Button> : <Text key={i}>{page}</Text>}
          </Box>
        )
      })}
    </Box>
  )
}

PageOptions.propTypes = {
  pageOptions: PropTypes.array,
  gotoPage: PropTypes.func,
  pageIndex: PropTypes.number,
  loading: PropTypes.bool
}

function Pagination({
  pageIndex,
  pageCount,
  pageSize,
  gotoPage,
  canPreviousPage,
  previousPage,
  canNextPage,
  nextPage,
  pageOptions,
  totalCount,
  loading
}) {
  return (
    <Box
      borderBottomColor="colorBorderMain"
      borderCollapse="collapse"
      borderSpacing="0px"
      borderBottomStyle="solid"
      borderBottomWidth="1px"
      borderTopWidth="0px"
      height="48px"
      pl="spaceGapMedium"
      bg="colorFillMain"
      display='flex'
      justifyContent='center'
      alignItems='center'
      margin='0 -16px'>
      <Text variant="body2" fontWeight="fontWeightBold" display="inline-flex">
        {pageIndex * pageSize + 1} - {Math.min(
          (pageIndex + 1) * pageSize,
          pageIndex * pageSize + pageSize, totalCount
        )}
        <Text fontWeight="fontWeightNormal" variant="body2">
        &nbsp;of&nbsp;
        </Text>
        {totalCount}
      </Text>
      <Button variant="secondaryText" onClick={() => gotoPage(0)} disabled={loading || !canPreviousPage} display="flex" alignItems="center" pr="0">
        <Icon name='minus' style={{ transform: 'rotate(90deg)' }} size="sm" mr="-5px" />
        <Icon name='angle-down' style={{ transform: 'rotate(90deg)' }} />
      </Button>
      <Button variant="secondaryText" onClick={() => previousPage()} disabled={loading || !canPreviousPage} display="flex" alignItems="center" pr="0">
        <Icon name='angle-down' style={{ transform: 'rotate(90deg)' }} />
      </Button>
      <Box>
        <PageOptions pageOptions={pageOptions} gotoPage={gotoPage} pageIndex={pageIndex} loading={loading} />
      </Box>
      <Button variant="secondaryText" onClick={() => nextPage()} disabled={loading || !canNextPage} display="flex" alignItems="center" pr="0">
        <Icon name='angle-down' style={{ transform: 'rotate(-90deg)' }} />
      </Button>
      <Button variant="secondaryText" onClick={() => gotoPage(pageCount - 1)} disabled={loading || !canNextPage} display="flex" alignItems="center" pr="0">
        <Icon name='angle-down' style={{ transform: 'rotate(-90deg)' }} />
        <Icon name='minus' style={{ transform: 'rotate(90deg)' }} size="sm" ml="-5px" />
      </Button>
    </Box>
  )
}

Pagination.propTypes = {
  pageIndex: PropTypes.number,
  pageCount: PropTypes.number,
  pageSize: PropTypes.number,
  gotoPage: PropTypes.func,
  canPreviousPage: PropTypes.bool,
  previousPage: PropTypes.func,
  canNextPage: PropTypes.bool,
  nextPage: PropTypes.func,
  pageOptions: PropTypes.array,
  totalCount: PropTypes.number,
  loading: PropTypes.bool
}

export default RequestsList
